import { convertRemToPx } from '@findigs/helpers/src/helpers/convert-rem-to-px';
import { useCallback, useMemo, useRef } from 'react';
import {
  FocusScope,
  Overlay,
  useDialog,
  useOverlayTrigger,
  usePopover,
} from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import type { OverlayTriggerState } from 'react-stately';
import { styles } from './navbar-popover.module.scss';

export type NavbarPopoverProps = {
  children?: JSX.Element;
  className?: string;
  state: OverlayTriggerState;
  triggerRef: React.RefObject<Element>;
  overlayProps: { id?: string };
};

export function useNavbarPopover() {
  const triggerRef = useRef(null);

  const lastState = useRef({ value: false, date: new Date() });

  const state = useOverlayTriggerState({
    onOpenChange(open) {
      if (open !== lastState.current.value) {
        lastState.current.value = open;
        lastState.current.date = new Date();
      }
    },
  });

  const toggle = useCallback(() => {
    if (Date.now() - lastState.current.date.valueOf() > 100) {
      state.toggle();
    }
  }, [state]);

  const scrollPaddingRight = useMemo(
    () =>
      typeof document !== 'undefined' && state.isOpen
        ? window.innerWidth - document.documentElement.clientWidth
        : 0,
    [state.isOpen]
  );

  const {
    triggerProps: { onPress, ...triggerProps },
    overlayProps,
  } = useOverlayTrigger({ type: 'dialog' }, state, triggerRef);

  return useMemo(
    () => ({
      state,
      toggle,
      triggerRef,
      triggerProps,
      overlayProps,
      scrollPaddingRight,
    }),
    [state, toggle, triggerProps, overlayProps, scrollPaddingRight]
  );
}

const NavbarPopoverInner = ({
  state,
  triggerRef,
  children,
  className,
  overlayProps,
}: NavbarPopoverProps) => {
  const popoverRef = useRef(null);
  const dialogRef = useRef(null);

  const { popoverProps } = usePopover(
    {
      popoverRef,
      triggerRef,
      shouldFlip: false,
      placement: 'bottom right',
      offset: convertRemToPx(0.5),
    },
    state
  );

  const { dialogProps } = useDialog({}, dialogRef);

  return (
    <Overlay>
      <FocusScope contain restoreFocus autoFocus>
        <div
          {...popoverProps}
          ref={popoverRef}
          className={`${styles} ${className ?? ''}`}
        >
          <div {...dialogProps} {...overlayProps} ref={dialogRef}>
            {children}
          </div>
        </div>
      </FocusScope>
    </Overlay>
  );
};

export const NavbarPopover = ({ state, ...props }: NavbarPopoverProps) => {
  return state.isOpen ? <NavbarPopoverInner {...props} state={state} /> : null;
};
